// Copyright Abridged, Inc. 2023,2024. All Rights Reserved.
// Node module: @collabland/sdk
// This file is licensed under the MIT License.
// License text available at https://opensource.org/license/mit/

import {model, property} from '@loopback/repository';
import {MiniAppVersion} from './mini-app-version.model.js';

/**
 * The model class is generated from OpenAPI schema - MiniAppManifest
 * MiniAppManifest
 */
@model({name: 'MiniAppManifest'})
export class MiniAppManifest {
  constructor(data?: Partial<MiniAppManifest>) {
    if (data != null && typeof data === 'object') {
      Object.assign(this, data);
    }
  }

  /**
   * Unique Id for Mini App
   */
  @property({
    required: true,
    jsonSchema: {
      type: 'string',
      description: 'Unique Id for Mini App',
    },
  })
  appId: string;

  /**
   * Client id for the Mini App to request API access
   */
  @property({
    jsonSchema: {
      type: 'string',
      description: 'Client id for the Mini App to request API access',
    },
  })
  clientId?: string;

  /**
   * App type for Mini App
   */
  @property({
    required: true,
    jsonSchema: {
      type: 'string',
      description: 'App type for Mini App',
    },
  })
  appType: string;

  /**
   * The developer for the Mini App
   */
  @property({
    required: true,
    jsonSchema: {
      type: 'string',
      description: 'The developer for the Mini App',
    },
  })
  developer: string;

  /**
   * Name for the Mini App
   */
  @property({
    required: true,
    jsonSchema: {
      type: 'string',
      description: 'Name for the Mini App',
    },
  })
  name: string;

  /**
   * Short Name for the Mini App
   */
  @property({
    required: true,
    jsonSchema: {
      type: 'string',
      description: 'Short Name for the Mini App',
    },
  })
  shortName: string;

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
  })
  supportedEnvs?: string[];

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
  })
  supportedCommunities?: string[];

  /**
   * Release date
   */
  @property({
    jsonSchema: {
      type: 'number',
      description: 'Release date',
    },
  })
  releasedDate?: number;

  /**
   *
   */
  @property({
    jsonSchema: {
      $ref: '#/components/schemas/MiniAppVersion',
    },
  })
  version?: MiniAppVersion;

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
        description: 'Keywords to do search and indexing',
      },
    },
  })
  keywords?: string[];

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
        description: 'Tags to do search and indexing',
      },
    },
  })
  tags?: string[];

  /**
   * Category of the Mini App it can be bot_command or cc_extension.
   */
  @property({
    jsonSchema: {
      type: 'string',
      description:
        ' Category of the Mini App it can be bot_command or cc_extension.',
    },
  })
  category?: string;

  /**
   * website url of the mini app docs or homepage
   */
  @property({
    jsonSchema: {
      type: 'string',
      description: 'website url of the mini app docs or homepage',
    },
  })
  website?: string;

  /**
   * Short Description of the mini app which will reflect on listing page
   */
  @property({
    jsonSchema: {
      type: 'string',
      description:
        'Short Description of the mini app which will reflect on listing page',
    },
  })
  shortDescription?: string;

  /**
   * This can be markdown or docs for what Mini App can do and what is it for?
   */
  @property({
    jsonSchema: {
      type: 'string',
      description:
        'This can be markdown or docs for what Mini App can do and what is it for?',
    },
  })
  description?: string;

  /**
   *
   */
  @property({
    jsonSchema: {
      type: 'array',
      items: {
        type: 'object',
        description: 'Images of Mini app',
      },
    },
  })
  thumbnails?: {}[];

  /**
   *
   */
  @property({
    jsonSchema: {
      type: 'array',
      items: {
        type: 'object',
        description: 'Icons of the Mini App',
      },
    },
  })
  icons?: {}[];

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
        description:
          'String of Array to get the platforms this app is available.',
      },
    },
  })
  platforms?: string[];

  /**
   * Type as free, paid or subscription based
   */
  @property({
    jsonSchema: {
      type: 'string',
      description: 'Type as free, paid or subscription based',
    },
  })
  type?: string;

  /**
   * Price of app in case of paid or subscription
   */
  @property({
    jsonSchema: {
      type: 'number',
      description: 'Price of app in case of paid or subscription',
    },
  })
  price?: number;

  /**
   *
   */
  @property.array(String, {
    jsonSchema: {
      type: 'array',
      items: {
        type: 'string',
        description: 'String of Array of subscribed events.',
      },
    },
  })
  subscribedEvents?: string[];

  /**
   * webhook URL
   */
  @property({
    jsonSchema: {
      type: 'string',
      description: 'webhook URL',
    },
  })
  webhookUrl?: string;

  /**
   * Template, miniapp type
   */
  @property({
    jsonSchema: {
      type: 'string',
      description: 'Template, miniapp type',
    },
  })
  template?: string;

  /**
   * If app in beta mode
   */
  @property({
    jsonSchema: {
      type: 'boolean',
      description: 'If app in beta mode',
    },
  })
  isBeta?: boolean;

  /**
   * additionalProperties
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [additionalProperty: string]: any;
}

export interface MiniAppManifestRelations {
  // describe navigational properties here
}

export type MiniAppManifestWithRelations = MiniAppManifest &
  MiniAppManifestRelations;
